import router from '@/router';
import { THttpMethods } from './types';
import { useUI } from '@/store/ui';
import { useUsers } from './store/users'; 
import Node from '@/store/entity/Node';

import { ROUT_AUTH, API_URL, TIMEZONE, ALLOW_FILE_FORMATS, ROUT_FOREIGN } from './constants';

const mixin = {
    methods: {
        fetch(url: string, options = {}) {
            return {
                async request(method: THttpMethods, data: any = null, customHeaders: any = null) {

                    // start loader
                    const ui = useUI();
                    // ui.ringLoader = true

                    if ( !localStorage.token && !['auth'].some(v => url.includes(v)) )  {
                        router.push(ROUT_AUTH);
                    }

                    let headers: any = {
                        'Authorization': localStorage.token,
                    };

                    if (data && !(data instanceof FormData)) {
                        headers['Content-Type'] = 'application/json';
                    }

                    if (customHeaders) headers = {...headers, ...customHeaders};

                    try {
                        const response = await fetch(API_URL + url, {
                            method: method,
                            headers: headers,
                            body: data instanceof FormData ? data : data ? JSON.stringify(data) : null
                        });

                        // ui.ringLoader = false;

                        if (response.status !== 200) {

                            if (response.status === 401) {
                                router.push(ROUT_AUTH);
                                localStorage.clear();
                            }

                            if (response.status === 403) {
                                const data = await response.json();
                                const user = data && data.user ? data.user : null;
                                if (user) useUsers().push(user);
                                router.push(ROUT_FOREIGN);
                            }

                            if (response.status >= 500) {
                                router.push('/500');
                            } else if (router.currentRoute.value.path === '/500') {
                                router.push('/');
                            }

                            const error = await response.json();
                            ui.setNotification('warning', error.message, 'fetch-error');
                            console.warn(error);
                            return null;

                        } else {
                            if (
                                router.currentRoute.value.path === '/500' ||
                                (router.currentRoute.value.path === ROUT_FOREIGN && useUsers().current && useUsers().current?.role !== 'foreign')
                            ) router.push('/');
                        }

                        if (ALLOW_FILE_FORMATS.includes(response.headers.get('Content-Type') ?? '')) {
                            return await response.blob();
                        } else {
                            return await response.json();
                        }

                    } catch (err: any) {
                        // ui.ringLoader = false;
                        console.error(err);
                        if (err.name === 'AbortError') {

                            // setTimeout(() => {
                            //     document.location.reload();
                            // }, 1000);

                        }
                    }

                },

                get() {
                    return this.request('GET');
                },

                post(data: any = null, noWs?: boolean) {
                    if (!useUI().wsOnline && !noWs) {
                        useUI().setNotification('error', "Перевірте інтернет з'єднання!", 'ws-error');
                        return null;
                    }
                    return this.request('POST', data);

                },

                patch(data: any = null, noWs?: boolean) {
                    if (!useUI().wsOnline && !noWs) {
                        useUI().setNotification('error', "Перевірте інтернет з'єднання!", 'ws-error');
                        return null;
                    }
                    return this.request('PATCH', data);
                },

                delete(data: any = null, noWs?: boolean) {
                    if (!useUI().wsOnline && !noWs) {
                        useUI().setNotification('error', "Перевірте інтернет з'єднання!", 'ws-error');
                        return null;
                    }
                    return this.request('DELETE', data);
                },
            }
        },

        getCurrentTime() {
            const formatter = new Intl.DateTimeFormat('uk', {
                timeZone: TIMEZONE,
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                timeZoneName: 'short',
            });
            return formatter.format(new Date());
        },

        getCurrentDateForPath() {
            const now = new Date();
            const pad = (num: any) => String(num).padStart(2, '0');
            const day = pad(now.getDate());
            const month = pad(now.getMonth() + 1); // Месяцы с 0 по 11
            const year = now.getFullYear();
            const hours = pad(now.getHours());
            const minutes = pad(now.getMinutes());
            return `${day}-${month}-${year}-${hours}-${minutes}`;
        },

        getFormatedDate(date: number, time = false, isFromDb = false) {

            const resultDateNum = isFromDb ? date * 1000 : date;
            const resultDate = new Date(resultDateNum);

            const opt: any = {
                year: "numeric",
                month: "numeric",
                day: "numeric",
            };
            if (time) {
                opt.hour = "2-digit";
                opt.minute = "2-digit";
                opt.second = "2-digit";
            }
            const formatter = new Intl.DateTimeFormat("uk", opt);
            return formatter.format(resultDate);
        },

        timestampToDateInputString(timestamp: number, isFromDb = true): string
        {
            if (isFromDb) timestamp = timestamp * 1000;
            const date = new Date(timestamp);
            const year = date.getUTCFullYear();
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const day = String(date.getUTCDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        },

        dateInputStringToTimestamp(dateString: string, isForeDb = true): number
        {
            const [year, month, day] = dateString.split('-').map(Number);
            const date = new Date(Date.UTC(year, month - 1, day));
            if (isForeDb) return date.getTime() / 1000;
            return date.getTime();
        },

        getHsize(size: number): string
        {
            if (size < 1000) return size + ' B'; // байты
            if (size > 1000 && size < 1000000) return (size / 1000).toFixed(1) + ' kB'; // килобайты
            if (size > 1000000 && size < 1000000000) return (size / 1000000).toFixed(1) + ' MB'; // мегабайты
            else return (size / 1000000000).toFixed(1) + 'GB';
        },

        downloadDone(data: any, downloadNodes: Node[]) {

            let fileName = 'archive-' + this.getCurrentDateForPath() + '.zip';

            if (downloadNodes.length === 1 && downloadNodes[0].type === 'file') {
                fileName = downloadNodes[0].name;
            } else if (downloadNodes.length === 1 && downloadNodes[0].type === 'folder') {
                fileName = downloadNodes[0].name + '.zip';
            }

            const blob = new Blob([data]);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        },

        isValidFolderName(data: string) {
            // !!! не убирать
            // eslint-disable-next-line no-control-regex
            return /^[^<>:"/\\|?*\x00-\x1F]+$/.test(data);
        } 
    }
};

export const methods = mixin.methods;

export default mixin;